// docs/59 CC6: a ranger's companion fights as a unit of its own under its ranger's hero id with
// bit 40 set (`fk_sim::companion`). The ranger's controller plans for it like a second hero.

/** `fk_sim::COMPANION_BIT`: 2^40, inside JavaScript's safe integers. */
export const COMPANION_BIT = 2 ** 40;

/** Calling ids of the eight companions (`fk_grammar::CALLINGS` 31–38). */
export const isCompanionPick = (pick: number): boolean => pick >= 31 && pick <= 38;

export const isCompanionId = (heroId: number): boolean => heroId >= COMPANION_BIT;

/** The companion's hero id for ranger `heroId`. */
export const companionIdOf = (heroId: number): number => heroId + COMPANION_BIT;

/** The hero row behind a unit's hero id: the ranger for a companion, itself otherwise. */
export const rangerOf = (heroId: number): number => (isCompanionId(heroId) ? heroId - COMPANION_BIT : heroId);

/** The beast's kind by calling id, for the roster's class line. */
/** The Oryx frame of `body`'s coat `coat` (`fk_grammar::CompanionDef::coat_frame`); an unknown coat reads as the first. */
export const coatFrame = (body: { frame: number; coats: { frame: number }[] }, coat: number): number => body.coats[coat]?.frame ?? body.frame;

/** The last frame of the Oryx pack in the creatures atlas; the commissioned companions follow (tools/oryx-extract.py). */
export const ORYX_CREATURES = 396;

/** A creature's frame on the off beat: an Oryx creature's B frame sits 18 after its A; a
 *  commissioned companion has one frame and stays on it. */
export const offBeat = (frame: number, on: boolean): number => (on && frame <= ORYX_CREATURES ? frame + 18 : frame);

export const COMPANION_KINDS: Record<number, string> = { 31: 'Wolf', 32: 'Bear', 33: 'Raven', 34: 'Cat', 35: 'Owl', 36: 'Otter', 37: 'Bat', 38: 'Weasel' };

/** docs/118 PT9: heroes in the board's order, each ranger's companion right after its ranger (a beast whose ranger is not on the board comes last). */
export function besideRangers<T extends { heroId?: number }>(units: readonly T[]): T[] {
  const beasts = units.filter((u) => isCompanionId(u.heroId!));
  const placed = new Set<T>();
  const out: T[] = [];
  for (const h of units) {
    if (isCompanionId(h.heroId!)) continue;
    out.push(h);
    for (const b of beasts) if (rangerOf(b.heroId!) === h.heroId) { out.push(b); placed.add(b); }
  }
  return [...out, ...beasts.filter((b) => !placed.has(b))];
}
